import React, { useState, useEffect } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import MetricCard from './components/MetricCard';

import AlertFeed from './components/AlertFeed';
import ConcernAnalysis from './components/ConcernAnalysis';
import ConcernProductAnalysis from './components/ConcernProductAnalysis';
import KPITilesSection from './components/KPITilesSection';
import MinutesBalanceWidget from './components/MinutesBalanceWidget';
import PrimaryGraphCard from './components/PrimaryGraphCard';
import TreemapLayoutSection from './components/TreemapLayoutSection';

const AnalyticsOverview = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const metricsData = [
  {
    title: "AI Resolution Rate",
    value: "87.3",
    unit: "%",
    change: 5.2,
    changeType: "positive",
    icon: "Bot",
    iconColor: "var(--color-success)",
    sparklineData: [82, 84, 83, 85, 86, 87, 87.3]
  },
  {
    title: "Avg Response Time",
    value: "2.4",
    unit: "sec",
    change: -12.5,
    changeType: "positive",
    icon: "Clock",
    iconColor: "var(--color-primary)",
    sparklineData: [3.2, 3.0, 2.8, 2.7, 2.6, 2.5, 2.4]
  },
  {
    title: "Customer Satisfaction",
    value: "4.6",
    unit: "/5",
    change: 3.8,
    changeType: "positive",
    icon: "Star",
    iconColor: "var(--color-warning)",
    sparklineData: [4.3, 4.4, 4.5, 4.5, 4.6, 4.6, 4.6]
  },
  {
    title: "Cost Per Resolution",
    value: "$1.23",
    unit: "",
    change: -8.3,
    changeType: "positive",
    icon: "DollarSign",
    iconColor: "var(--color-secondary)",
    sparklineData: [1.45, 1.40, 1.35, 1.30, 1.28, 1.25, 1.23]
  },
  {
    title: "Total Call Volume",
    value: "12,847",
    unit: "",
    change: 15.7,
    changeType: "positive",
    icon: "Phone",
    iconColor: "var(--color-primary)",
    sparklineData: [10200, 10800, 11200, 11600, 12000, 12400, 12847]
  },
  {
    title: "Human Escalation Rate",
    value: "12.7",
    unit: "%",
    change: -2.1,
    changeType: "positive",
    icon: "AlertTriangle",
    iconColor: "var(--color-error)",
    sparklineData: [15, 14.5, 14, 13.5, 13, 12.9, 12.7]
  }];


  const chartData = [
  { period: "Mon", callVolume: 1842, resolutionRate: 85.2 },
  { period: "Tue", callVolume: 2156, resolutionRate: 86.8 },
  { period: "Wed", callVolume: 1923, resolutionRate: 87.1 },
  { period: "Thu", callVolume: 2341, resolutionRate: 88.3 },
  { period: "Fri", callVolume: 2089, resolutionRate: 87.9 },
  { period: "Sat", callVolume: 1567, resolutionRate: 86.5 },
  { period: "Sun", callVolume: 1929, resolutionRate: 87.3 }];


  const alertsData = [
  {
    id: 1,
    severity: "critical",
    title: "High Escalation Rate Detected",
    message: "Agent AI-Support-03 showing 28% escalation rate in last hour",
    timestamp: new Date(Date.now() - 300000),
    metrics: [
    { label: "Agent", value: "AI-Support-03" },
    { label: "Rate", value: "28%" }]

  },
  {
    id: 2,
    severity: "warning",
    title: "Response Time Spike",
    message: "Average response time increased to 4.2 seconds during peak hours",
    timestamp: new Date(Date.now() - 900000),
    metrics: [
    { label: "Current", value: "4.2s" },
    { label: "Baseline", value: "2.4s" }]

  },
  {
    id: 3,
    severity: "info",
    title: "New Peak Volume Record",
    message: "Highest daily call volume achieved: 12,847 calls processed successfully",
    timestamp: new Date(Date.now() - 1800000),
    metrics: [
    { label: "Volume", value: "12,847" },
    { label: "Previous", value: "11,203" }]

  },
  {
    id: 4,
    severity: "warning",
    title: "Knowledge Base Gap Identified",
    message: "15 calls escalated due to missing information about new product features",
    timestamp: new Date(Date.now() - 3600000),
    metrics: [
    { label: "Escalations", value: "15" },
    { label: "Topic", value: "Product Features" }]

  }];


  const concernsData = [
  {
    id: 1,
    category: "Billing Issues",
    description: "Questions about charges, refunds, and payment methods",
    volume: 2847,
    percentage: 22.1,
    resolutionRate: 91.3,
    avgCallDuration: 3,
    humanEscalation: 23,
    sentiment: "neutral",
    trend: -3.2
  },
  {
    id: 2,
    category: "Technical Support",
    description: "App crashes, login problems, and feature malfunctions",
    volume: 2341,
    percentage: 18.2,
    resolutionRate: 84.7,
    avgCallDuration: 3,
    humanEscalation: 23,
    sentiment: "negative",
    trend: 5.8
  },
  {
    id: 3,
    category: "Account Management",
    description: "Profile updates, password resets, and account settings",
    volume: 1923,
    percentage: 15.0,
    resolutionRate: 95.2,
    avgCallDuration: 3,
    humanEscalation: 23,
    sentiment: "positive",
    trend: -1.5
  },
  {
    id: 4,
    category: "Product Information",
    description: "Feature inquiries, pricing questions, and plan comparisons",
    volume: 1756,
    percentage: 13.7,
    resolutionRate: 88.9,
    avgCallDuration: 3,
    humanEscalation: 23,    
    sentiment: "positive",
    trend: 2.3
  },
  {
    id: 5,
    category: "Shipping & Delivery",
    description: "Order tracking, delivery delays, and shipping costs",
    volume: 1589,
    percentage: 12.4,
    resolutionRate: 86.4,
    avgCallDuration: 3,
    humanEscalation: 23,    
    sentiment: "neutral",
    trend: 1.2
  },
  {
    id: 6,
    category: "Cancellations",
    description: "Subscription cancellations and service termination requests",
    volume: 1391,
    percentage: 10.8,
    resolutionRate: 78.5,
    avgCallDuration: 3,
    humanEscalation: 23,    
    sentiment: "negative",
    trend: 4.7
  }];


  const handleDrillDown = (action) => {
    console.log(`Drill-down action triggered: ${action}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <NavigationBreadcrumb />
        <div className="flex items-center justify-center h-[calc(100vh-128px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content">
        <div className="min-h-screen bg-background">
          <NavigationBreadcrumb />
          <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Analytics Overview
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Monitor AI customer support performance and business intelligence metrics in real-time
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <MinutesBalanceWidget />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              {metricsData?.map((metric, index) =>
              <MetricCard key={index} {...metric} />
              )}
            </div>

            <KPITilesSection />

            <PrimaryGraphCard />

            <div className="mb-6 md:mb-8">
              <AlertFeed alerts={alertsData} />
            </div>

            <div className="mb-6 md:mb-8">
            <ConcernAnalysis concerns={concernsData} />
            </div>
            
            <div className="mb-6 md:mb-8">
            <ConcernProductAnalysis concerns={concernsData} />
            </div>
            {/* <TreemapLayoutSection /> */}
          </main>
        </div>
      </div>
    </>);

};

export default AnalyticsOverview;